/** Panels for scenarios computed ahead of time, for a metro too large to
 *  simulate on request. The numbers come from the metro's pack
 *  (/api/traffic/prebaked); the map's per-edge change from /api/traffic/compare. */

import { useId, useState } from 'react';
import { FaTimes, FaSignOutAlt, FaSpinner, FaUndo } from 'react-icons/fa';
import { VC_DELTA_LEGEND } from '../utils/networkEditor';
import { groupName, groupsOf, corridorName, ratioText, noteText, BAND_OUTLINE } from '../utils/corridors';
import { DEFAULT_SERVICE_LEVEL, levelOf, levelText, packLevels, idAtLevel } from '../utils/serviceLevels';
import { isLiveSimulation } from '../config';
import ServiceLevelToggle from './ServiceLevelToggle';
import './PrebakedScenarios.css';

const MINUS = '−';

function pct(a, b, digits = 1) {
  if (!a) return '—';
  const v = ((b - a) / a) * 100;
  const s = Math.abs(v).toFixed(digits);
  return `${v < 0 ? MINUS : '+'}${s}%`;
}
const int = (n) => Math.round(n).toLocaleString();
const num = (n) => (Math.round(n) < 0 ? `${MINUS}${int(-n)}` : int(n));
const share = (x) => (x === null || x === undefined ? '—' : `${(x * 100).toFixed(1)}%`);
const USD0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const USD2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const usd = (x) => USD0.format(x);
const usd2 = (x) => USD2.format(x);
const MILES = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 });
const WHOLE = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const WAIT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const signed = (d, fmt) => (Math.abs(d) < 1e-9 ? '±0' : `${d < 0 ? MINUS : '+'}${fmt(Math.abs(d))}`);

function DeltaLegend({ from = 'baseline', to = 'scenario' }) {
  return (
    <div className="run-legend">
      <div className="run-legend-title">V/C change on the map ({from} → {to})</div>
      {VC_DELTA_LEGEND.map((b) => (
        <div className="run-legend-row" key={b.label}>
          <span className="run-legend-swatch" style={{ background: `rgb(${b.color.join(',')})` }} />
          <span>{b.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Loading / error / what the map is showing, for one comparison. */
function ResultStatus({ result }) {
  if (!result) return null;
  if (result.loading) {
    return (
      <div className="pb-status" aria-live="polite">
        <FaSpinner className="ne-spin" size={11} /> Loading the result…
      </div>
    );
  }
  if (result.error) return <div className="macro-run-error" aria-live="polite">{result.error}</div>;
  const n = result.features.length;
  return (
    <div className="pb-status" aria-live="polite">
      Map: {n.toLocaleString()} {result.truncated ? 'largest changes' : 'road segments'} with V/C
      {' '}changing by 0.02 or more.
    </div>
  );
}

const pad = (h) => String(h).padStart(2, '0');
const hourSpan = (h) => `${pad(h)}:00–${pad((h + 1) % 24)}:00`;
const charged = (hour, windows) => windows.some(([a, b]) => a <= hour && hour < b);

/** A scenario at one hour of the day: its figures, or why there are none. */
export function scenarioHour(s, hour, windows) {
  const at = s.hours[String(hour)];
  if (at) return { kind: 'computed', at };
  if (!charged(hour, windows)) return { kind: 'not-charged' };
  if (s.hours_scope && !s.hours_scope.includes(hour)) return { kind: 'out-of-scope', note: s.hours_note };
  return { kind: 'not-computed' };
}

function ScenarioButton({ n, label, active, onClick }) {
  return (
    <button type="button" className={`pb-scenario ${active ? 'active' : ''}`}
      aria-pressed={active} onClick={onClick}>
      <span className="pb-scenario-n">{n}</span>{label}
    </button>
  );
}

/** The zone with nothing charged, at `hour`: what every scenario starts from.
 *  From a bake at the hours one exists, else from the run's own baseline. */
function BaselineHour({ cp, hour, day }) {
  const at = cp.scenarios.map((s) => s.hours[String(hour)]).find(Boolean);
  const run = !at && day?.hours ? day.hours.find((x) => x.hour === hour) : null;
  if (run) {
    return (
      <div className="macro-run-result pb-result">
        <div className="pb-hour-head">{hourSpan(hour)} · no charge</div>
        <div className="macro-run-grid">
          <div><span>{int(run.entries)}</span>entries into the zone</div>
          <div><span>{int(run.vmtMi)}</span>vehicle miles, whole region</div>
          {run.vehicles !== null && <div><span>{int(run.vehicles)}</span>vehicles on the network</div>}
        </div>
        <div className="pb-status">
          {run.charged ? 'No pricing scenario was computed for this hour.'
            : 'Not charged in this hour: every pricing scenario equals the baseline.'}
        </div>
      </div>
    );
  }
  if (!at) {
    return <div className="pb-line pb-state">{hourSpan(hour)}: no scenario was computed for this hour.</div>;
  }
  const b = at.baseline;
  return (
    <div className="macro-run-result pb-result">
      <div className="pb-hour-head">{hourSpan(hour)} · no charge</div>
      <div className="macro-run-grid">
        <div><span>{int(b.entering_vehicles)}</span>vehicles entering the zone</div>
        <div><span>{int(b.entries)}</span>entries into the zone</div>
        <div><span>{int(b.zone_vmt_mi)}</span>vehicle miles inside the zone</div>
        <div><span>{int(b.total_vmt_mi)}</span>vehicle miles, whole region</div>
        <div><span>{int(b.vehicles)}</span>vehicles on the network</div>
        <div><span>{int(b.congested_edges)}</span>congested road segments</div>
      </div>
      <div className="pb-status">Pick a scenario to see what it changes on the map.</div>
    </div>
  );
}

/** One scenario against no charge. */
function VsBaseline({ at }) {
  const a = at.baseline;
  const b = at.scenario;
  const ms = at.mode_shift;
  return (
    <div className="macro-run-grid">
      <div><span>{pct(a.entering_vehicles, b.entering_vehicles)}</span>vehicles entering the zone</div>
      <div><span>{share(ms.removed_share)}</span>of car trips into the zone switch to transit</div>
      <div><span>{pct(a.zone_vmt_mi, b.zone_vmt_mi)}</span>vehicle miles inside the zone</div>
      <div><span>{pct(a.total_vmt_mi, b.total_vmt_mi, 2)}</span>vehicle miles, whole region</div>
      <div><span>{share(ms.drive_share_before)} → {share(ms.drive_share_after)}</span>drive share of trips into the zone</div>
      <div><span>{int(b.rerouted_vehicles)}</span>vehicles take another route</div>
      <div><span>{usd(at.paid_usd)}</span>tolls paid in this hour</div>
      <div><span>{usd2(at.paid_per_entering_vehicle_usd)}</span>average toll per entering vehicle</div>
    </div>
  );
}

/** One scenario against the scenario it changes one thing of. */
function VsScenario({ at, other }) {
  const a = other.scenario;
  const b = at.scenario;
  return (
    <div className="macro-run-grid">
      <div><span>{pct(a.entering_vehicles, b.entering_vehicles)}</span>vehicles entering the zone</div>
      <div><span>{int(other.mode_shift.removed)} → {int(at.mode_shift.removed)}</span>car trips into the zone switch to transit</div>
      <div><span>{pct(a.zone_vmt_mi, b.zone_vmt_mi)}</span>vehicle miles inside the zone</div>
      <div><span>{pct(a.total_vmt_mi, b.total_vmt_mi, 2)}</span>vehicle miles, whole region</div>
      <div><span>{usd(other.paid_usd)} → {usd(at.paid_usd)}</span>tolls paid in this hour</div>
      <div><span>{usd2(other.paid_per_entering_vehicle_usd)} → {usd2(at.paid_per_entering_vehicle_usd)}</span>average toll per entering vehicle</div>
    </div>
  );
}

/** Per income class: who enters, who switches to transit, who pays what. Against
 *  another scenario, each figure carries its change from that scenario's same
 *  class, when that scenario's per-class figures exist. */
function ClassTable({ labels, order, rows, otherRows, otherName }) {
  const versus = otherName !== null;
  const sum = (rs, key) => order.reduce((acc, c) => acc + rs[c][key], 0);
  const line = (r, u) => ({
    entering: r.entering_vehicles,
    enteringNote: u ? signed(r.entering_vehicles - u.entering_vehicles, int)
      : versus ? null : pct(r.entering_vehicles_baseline, r.entering_vehicles),
    removed: r.removed,
    removedNote: u ? signed(r.removed - u.removed, int) : null,
    paid: r.paid_usd,
    paidNote: u ? signed(r.paid_usd - u.paid_usd, usd) : null,
    per: r.paid_per_entering_vehicle_usd,
    perNote: u ? signed(r.paid_per_entering_vehicle_usd - u.paid_per_entering_vehicle_usd, usd2) : null,
  });
  const total = (rs) => {
    const entering = sum(rs, 'entering_vehicles');
    const paid = sum(rs, 'paid_usd');
    return { entering_vehicles: entering, entering_vehicles_baseline: sum(rs, 'entering_vehicles_baseline'),
             removed: sum(rs, 'removed'), paid_usd: paid,
             paid_per_entering_vehicle_usd: entering ? paid / entering : 0 };
  };
  const cell = (value, note) => (
    <td>{value}{note && <span className="pb-class-note">{note}</span>}</td>
  );
  const body = (key, head, l) => (
    <tr key={key} className={key === 'total' ? 'pb-classes-total' : undefined}>
      {head}
      {cell(int(l.entering), l.enteringNote)}
      {cell(int(l.removed), l.removedNote)}
      {cell(usd(l.paid), l.paidNote)}
      {cell(usd2(l.per), l.perNote)}
    </tr>
  );
  return (
    <>
      <table>
        <thead>
          <tr>
            <th scope="col">Income</th><th scope="col">Entering</th><th scope="col">To transit</th>
            <th scope="col">Tolls paid</th><th scope="col">Per vehicle</th>
          </tr>
        </thead>
        <tbody>
          {order.map((c) => body(c, (
            <th scope="row">
              <span className="pb-class-name">{labels[c]?.name || c}</span>
              <span className="pb-class-sub">
                {rows[c].toll_usd > 0 ? usd2(rows[c].toll_usd) : 'free'}
                {labels[c]?.income ? ` · ${labels[c].income}` : ''}
              </span>
            </th>
          ), line(rows[c], otherRows ? otherRows[c] : null)))}
          {body('total', <th scope="row"><span className="pb-class-name">All</span></th>,
                line(total(rows), otherRows ? total(otherRows) : null))}
        </tbody>
      </table>
      <div className="pb-classes-foot">
        {!versus ? 'Small figures: change in entering vehicles from no charge.'
          : otherRows ? `Small figures: change from ${otherName}.`
          : `Per-class figures for ${otherName} are not computed yet.`}
      </div>
    </>
  );
}

/** The class table at the hour shown or over the hours the scenario's totals
 *  cover; a switch between the two only when both exist. */
function ClassSection({ labels, order, hourRows, hourOther, dayRows, dayOther, otherName, hourWhen, dayWhen }) {
  const [view, setView] = useState('hour');
  const shown = hourRows && (view === 'hour' || !dayRows) ? 'hour' : dayRows ? 'day' : null;
  if (!shown) return null;
  const both = !!(hourRows && dayRows);
  return (
    <div className="pb-classes">
      <div className="pb-classes-bar">
        <span className="pb-classes-head">By income class{both ? '' : `, ${shown === 'hour' ? hourWhen : dayWhen}`}</span>
        {both && (
          <div className="pb-span-toggle" role="group" aria-label="Income class figures for">
            {[['hour', hourWhen], ['day', dayWhen]].map(([key, label]) => (
              <button key={key} type="button" className={`pb-span-btn ${shown === key ? 'active' : ''}`}
                aria-pressed={shown === key} onClick={() => setView(key)}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <ClassTable labels={labels} order={order} otherName={otherName}
        rows={shown === 'hour' ? hourRows : dayRows} otherRows={shown === 'hour' ? hourOther : dayOther} />
    </div>
  );
}

/** '6 am–9 am and 3 pm–6 pm' for [6, 7, 8, 15, 16, 17]; a window named in `labels`
 *  ('15-18') shows its name instead of its hours. */
function hoursText(hours, labels) {
  const runs = [];
  for (const h of [...hours].sort((a, b) => a - b)) {
    const last = runs[runs.length - 1];
    if (last && last[1] === h) last[1] = h + 1;
    else runs.push([h, h + 1]);
  }
  return windowsText(runs, labels);
}

const windowsText = (windows, labels = {}) => windows
  .map(([a, b]) => labels[`${a}-${b}`] || `${fmtHour(a)}–${fmtHour(b)}`).join(' and ');

/** One figure from → to, with its change when both are counts. */
function FromTo({ from, to, fmt, digits }) {
  if (from === null || from === undefined) return <>{fmt(to)}</>;
  return (
    <>
      {fmt(from)} → {fmt(to)}
      {digits !== undefined && <span className="pb-day-pct">{pct(from, to, digits)}</span>}
    </>
  );
}

/** Totals over the day, or over the charged hours a scenario is computed for. */
function DayTotals({ day, chosen, other, labels }) {
  if (!day) return null;
  if (day.loading) {
    return <div className="pb-day pb-status" aria-live="polite">Loading the totals for the day…</div>;
  }
  if (day.error) return <div className="pb-day pb-status" aria-live="polite">{day.error}</div>;
  const lacking = day.missingBaseline.length;
  if (!chosen) {
    if (!day.baseline) {
      return <div className="pb-day pb-line">All-day totals: the baseline has no result for {lacking} {lacking === 1 ? 'hour' : 'hours'}.</div>;
    }
    return (
      <section className="pb-day" aria-label="All day with no charge">
        <div className="pb-day-head">All day · no charge</div>
        <table>
          <tbody>
            <tr><th scope="row">Vehicle miles, whole region</th><td>{MILES.format(day.baseline.vmtMi)}</td></tr>
            <tr><th scope="row">Entries into the zone</th><td>{WHOLE.format(day.baseline.entries)}</td></tr>
          </tbody>
        </table>
      </section>
    );
  }
  const ds = day.scenarios.find((x) => x.id === chosen.id);
  if (!ds) return null;
  const when = ds.wholeDay ? 'All day' : hoursText(ds.scope, labels);
  const t = ds.totals;
  if (!t) {
    const what = ds.wholeDay ? 'All-day totals' : `Totals for ${when}`;
    return (
      <div className="pb-day pb-line" aria-live="polite">
        {ds.missing.length
          ? `${what}: ${ds.computed.length} of ${ds.scope.length} charged hours computed.`
          : `${what}: the baseline has no result for ${lacking} ${lacking === 1 ? 'hour' : 'hours'}.`}
      </div>
    );
  }
  const otherName = other ? `${other.short.toLowerCase()} pricing` : 'no charge';
  if (other && !t.against) {
    return <div className="pb-day pb-line">{when}: {otherName} is not computed for every one of these hours.</div>;
  }
  const from = other ? t.against : t.baseline;
  const s = t.scenario;
  return (
    <section className="pb-day" aria-label={`${when}, against ${otherName}`}>
      <div className="pb-day-head">{when} · against {otherName}</div>
      <table>
        <tbody>
          <tr><th scope="row">Vehicle miles, whole region</th>
            <td><FromTo from={from.vmtMi} to={s.vmtMi} fmt={(x) => MILES.format(x)} digits={2} /></td></tr>
          <tr><th scope="row">Entries into the zone</th>
            <td><FromTo from={from.entries} to={s.entries} fmt={(x) => WHOLE.format(x)} digits={1} /></td></tr>
          <tr><th scope="row">Tolls paid</th>
            <td><FromTo from={other ? from.paidUsd : null} to={s.paidUsd} fmt={usd} /></td></tr>
          <tr><th scope="row">Car trips switched to transit</th>
            <td><FromTo from={other ? from.toTransit : null} to={s.toTransit} fmt={(x) => WHOLE.format(x)} /></td></tr>
        </tbody>
      </table>
      <div className="pb-day-note">
        {ds.wholeDay
          ? `Charged ${hoursText(day.chargedHours, labels)}; every other hour equals the baseline.`
          : `${chosen.short} pricing is computed for ${when} only.`}
        {' '}Each hour is modelled on its own, so switches to transit are counted per trip.
      </div>
    </section>
  );
}

function BandsLegend({ state }) {
  return (
    <div className="pb-bands-legend">
      <div className="run-legend-row">
        <span className="pb-swatch pb-swatch-outline"
          style={{ borderColor: `rgb(${BAND_OUTLINE.join(',')})`, backgroundColor: `rgba(${BAND_OUTLINE.join(',')}, 0.25)` }} />
        <span>Protected neighborhood area</span>
      </div>
      {state?.loading && <div className="pb-status"><FaSpinner className="ne-spin" size={11} /> Loading the bands…</div>}
      {state?.error && <div className="macro-run-error">{state.error}</div>}
    </div>
  );
}

/** What a band lost (L), its neighbours gained (G) and G/L, as the bake gives them. */
const lgText = (vi) => `L ${num(vi.corridor_vehicles_lost)} · G ${vi.parallel_vehicles_gained === null ? '—' : num(vi.parallel_vehicles_gained)}`
  + ` · G/L ${ratioText(vi.gained_over_lost)}`;

/** A route-cost scenario's corridors at `hour`, or over all its hours: per band, the
 *  vehicles on each group's streets in route_cost.order's columns (no charge, the
 *  scenario compared against, this one); what the band lost (L) and its neighbours
 *  gained (G). The rule's inputs, and a verdict only where the bake writes one. */
function CorridorSection({ s, other, hourAt, hour, bands, onBands, labels }) {
  const [span, setSpan] = useState('hour');
  const total = s.route_cost_total;
  const fig = span === 'day' && total ? total : hourAt.route_cost;
  const label = (col) => (col === 'baseline' ? ['No charge', 'No charge']
    : col === s.id ? [String(s.n), s.short] : [String(other ? other.n : ''), other ? other.short : col]);
  const cols = s.route_cost.order;
  const vi = fig.verdict_inputs;
  const names = Object.keys(fig.corridors);
  const reported = names.some((n) => Object.keys(fig.corridors[n].reported || {}).length);
  return (
    <section className="pb-corridors" aria-label="Neighborhood bands">
      <div className="pb-classes-bar">
        <span className="pb-classes-head">Neighborhood bands</span>
        {total && (
          <div className="pb-span-toggle" role="group" aria-label="Band figures for">
            {[['hour', hourSpan(hour)], ['day', hoursText(total.hours, labels)]].map(([key, text]) => (
              <button key={key} type="button" className={`pb-span-btn ${span === key ? 'active' : ''}`}
                aria-pressed={span === key} onClick={() => setSpan(key)}>
                {text}
              </button>
            ))}
          </div>
        )}
      </div>
      {names.map((name) => {
        const c = fig.corridors[name];
        const street = s.route_cost.corridors[name]?.main_street;
        return (
          <div className="pb-corridor" key={name}>
            <div className="pb-corridor-name">{corridorName(name)}{street && <span className="pb-corridor-street">{street}</span>}</div>
            <table>
              <thead>
                <tr><th scope="col">Vehicles</th>{cols.map((col) => <th scope="col" key={col} title={label(col)[1]}>{label(col)[0]}</th>)}</tr>
              </thead>
              <tbody>
                {groupsOf(c).map((g) => (
                  <tr key={g.key}>
                    <th scope="row">{groupName(g, s.route_cost.factor)}</th>
                    {cols.map((col) => <td key={col}>{int(g.value.vehicles[col])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pb-corridor-lg">{lgText(c.verdict_inputs)}</div>
            {c.verdict_inputs.note && <div className="pb-line pb-warn">{noteText(c.verdict_inputs.note, hourSpan)}</div>}
          </div>
        );
      })}
      <div className="pb-corridor-all">
        {names.length === 2 ? 'Both bands' : 'All bands'}, each vehicle once: {lgText(vi)}.
        {' '}Against {other ? `${other.short.toLowerCase()} pricing` : s.compared_against}:
        {' '}entries into the zone {signed(vi.entries_change, int)}, vehicle miles {signed(vi.total_vmt_change_mi, int)}.
      </div>
      {vi.note && <div className="pb-line pb-warn">{noteText(vi.note, hourSpan)}</div>}
      {fig.verdict && <div className="pb-line">Verdict: {noteText(fig.verdict, hourSpan)}</div>}
      <div className="pb-day-note">
        Vehicles on a group&apos;s streets, each counted once in an hour. L is what the band streets lost
        against scenario {label(cols[1])[0]}, G what the same-class residential streets next to them gained;
        the method reads G ≥ 0.5 L as the burden moving onto those streets.
        {reported && ' The other groups are reported only: traffic moving onto them is the aim.'}
      </div>
      <label className="pb-bands-toggle">
        <input type="checkbox" checked={bands.on} onChange={(e) => onBands(e.target.checked)} />
        Outline the bands on the map
      </label>
      {bands.on && <BandsLegend state={bands.state} />}
    </section>
  );
}

const TRANSIT_SUMS = ['served', 'removed_by_toll_alone', 'extra_removed'];

/** A transit upgrade's figures summed over the hours that have them, lines merged by name. */
function transitTotal(entries) {
  const out = Object.fromEntries(TRANSIT_SUMS.map((k) => [k, 0]));
  const lines = {};
  for (const t of entries) {
    TRANSIT_SUMS.forEach((k) => { out[k] += t[k]; });
    for (const [name, l] of Object.entries(t.lines)) {
      const into = lines[name] || (lines[name] = { wait_saving_min: l.wait_saving_min, served: 0, removed: 0, extra_removed: 0 });
      into.served += l.served;
      into.removed += l.removed;
      into.extra_removed += l.extra_removed;
    }
  }
  return { ...out, walk_m: entries[0].walk_m, wait_weight: entries[0].wait_weight, lines };
}

/** 'SFMTA (Muni)|12' as '12 · SFMTA (Muni)'. */
const lineName = (key) => {
  const cut = key.lastIndexOf('|');
  return cut < 0 ? key : `${key.slice(cut + 1)} · ${key.slice(0, cut)}`;
};

/** A scenario whose tolls pay for more transit: the trips the upgraded lines serve, those
 *  the toll alone already moves, the further switches the shorter wait brings, at `hour`
 *  or over all the hours computed; and where the remaining drivers' routes come from. */
function TransitSection({ s, hourAt, hour, other, labels }) {
  const [span, setSpan] = useState('hour');
  const withTransit = Object.keys(s.hours).filter((h) => s.hours[h].transit).map(Number).sort((a, b) => a - b);
  const total = withTransit.length > 1 ? transitTotal(withTransit.map((h) => s.hours[String(h)].transit)) : null;
  const t = span === 'day' && total ? total : hourAt.transit;
  const rf = hourAt.routes_from;
  const otherName = other ? other.short.toLowerCase() : rf?.scenario || s.compared_against;
  const top = Object.entries(t.lines).filter(([, l]) => l.extra_removed > 0)
    .sort((a, b) => b[1].extra_removed - a[1].extra_removed).slice(0, 5);
  const nLines = Object.keys(t.lines).length;
  return (
    <section className="pb-transit" aria-label="Transit upgrade">
      <div className="pb-classes-bar">
        <span className="pb-classes-head">Transit upgrade</span>
        {total && (
          <div className="pb-span-toggle" role="group" aria-label="Transit figures for">
            {[['hour', hourSpan(hour)], ['day', hoursText(withTransit, labels)]].map(([key, text]) => (
              <button key={key} type="button" className={`pb-span-btn ${span === key ? 'active' : ''}`}
                aria-pressed={span === key} onClick={() => setSpan(key)}>
                {text}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="macro-run-grid">
        <div><span>{int(t.served)}</span>car trips into the zone start and end within {t.walk_m ?? 400} m of an upgraded line</div>
        <div><span>+{int(t.extra_removed)}</span>more trips switch to transit with the shorter wait</div>
        <div><span>{int(t.removed_by_toll_alone)}</span>switch under {otherName} pricing&apos;s tolls alone</div>
        <div><span>{nLines}</span>upgraded lines serve these trips</div>
      </div>
      {top.length > 0 && (
        <table className="pb-transit-lines">
          <thead>
            <tr><th scope="col">Line</th><th scope="col">Less waiting</th><th scope="col">More switch</th></tr>
          </thead>
          <tbody>
            {top.map(([name, l]) => (
              <tr key={name}>
                <th scope="row">{lineName(name)}</th>
                <td>{WAIT.format(l.wait_saving_min)} min</td>
                <td>+{int(l.extra_removed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="pb-day-note">
        A driver whose trip starts and ends within a walk of an upgraded line weighs the shorter wait
        {t.wait_weight ? ` at ${t.wait_weight}× in-vehicle time` : ''} in the same choice as {otherName} pricing,
        so some more switch. The Transit Investment panel lists every line and draws them on the map.
        {rf?.routes_held && (
          <>
            {' '}{span === 'hour' && rf.vehicles_removed_beyond != null
              ? `The ${int(rf.vehicles_removed_beyond)} further switches leave the road at ${hourSpan(hour)}; the drivers who stay`
              : 'The drivers who stay'} keep {otherName} pricing&apos;s routes: traffic was not re-assigned.
          </>
        )}
      </div>
    </section>
  );
}

const TENTHS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** A scenario's tolls and the further switches its transit brings, summed over its hours. */
function hourSums(s) {
  let paid = 0;
  let extra = 0;
  for (const at of Object.values(s.hours)) {
    paid += at.paid_usd;
    extra += at.transit ? at.transit.extra_removed : 0;
  }
  return { paid, extra };
}

/** A scenario at each of its service levels, a weekday: what the line ranking buys with the
 *  tolls of `funder` (transit_rank.json, `ranks`: { byLevel }, { loading } or { error }), and
 *  the switches and tolls of the level's own hours. A row picks its level. */
function LevelTable({ levels, value, ranks, funder, onPick }) {
  const titleId = useId();
  const byLevel = ranks?.byLevel || null;
  const m = byLevel?.[DEFAULT_SERVICE_LEVEL]?.method || null;
  // A line whose wait is already at the cap at a level is no candidate there, so the pool
  // differs by level (50 at +10%, 53 at +50%).
  const ranked = byLevel ? Object.values(byLevel).map((r) => r.method.lines_ranked) : [];
  const candidates = ranked.length
    ? ` (${Math.min(...ranked) === Math.max(...ranked) ? ranked[0] : `${Math.min(...ranked)}–${Math.max(...ranked)}`} candidates, by level)`
    : '';
  return (
    <section className="pb-levels" aria-labelledby={titleId}>
      <div className="inv-eq-title" id={titleId}>Service levels, a weekday</div>
      {m && (
        <div className="inv-eq-note">
          The same budget at each level: {usd(m.budget_usd_per_weekday)}{funder ? `, scenario ${funder.n}'s tolls` : ''}.
        </div>
      )}
      {ranks?.loading && (
        <div className="pb-status" aria-live="polite"><FaSpinner className="ne-spin" size={11} /> Loading the service levels…</div>
      )}
      {ranks?.error && <div className="macro-run-error" aria-live="polite">{ranks.error}</div>}
      <table className="inv-eq-table pb-levels-table" aria-labelledby={titleId}>
        <thead>
          <tr>
            <th scope="col">Service</th>
            <th scope="col">Lines</th>
            <th scope="col">Cost</th>
            <th scope="col">Budget left</th>
            <th scope="col" title="Further switches to transit, as the line ranking expects them">Expected switches</th>
            <th scope="col" title="Further switches to transit in the scenario's computed hours">Actual switches</th>
            <th scope="col">Tolls</th>
          </tr>
        </thead>
        <tbody>
          {levels.map(({ level, scenario }) => {
            const t = byLevel?.[level]?.totals;
            const own = hourSums(scenario);
            const on = level === value;
            return (
              <tr key={level} className={on ? 'active' : undefined} onClick={() => onPick(level)}>
                <th scope="row">
                  <button type="button" className="pb-level-pick" aria-pressed={on}>{levelText(level)}</button>
                </th>
                <td>{t ? t.lines_picked : '—'}</td>
                <td>{t ? usd(t.extra_cost_per_weekday_usd) : '—'}</td>
                <td>{t ? usd(t.budget_left_usd) : '—'}</td>
                <td>{t ? TENTHS.format(t.expected_extra_shifts_per_weekday) : '—'}</td>
                <td>{int(own.extra)}</td>
                <td>{usd(own.paid)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pb-day-note">
        Lines that stop within {`${m ? Math.round(m.walk_m) : 400} m`} of the charging zone{candidates};
        {' '}costs over the six charged hours. Not the same lines or budget as the Transit investment panel&apos;s region-wide presets.
      </div>
    </section>
  );
}

/** A chosen scenario at `hour`, against `other` (a scenario) or no charge. */
function ScenarioResult({ cp, s, other, hour, result }) {
  const state = scenarioHour(s, hour, cp.toll_windows_h);
  const span = hourSpan(hour);
  if (state.kind === 'not-charged') {
    return <div className="pb-line pb-state">Not charged at {span}: every pricing scenario equals the baseline in this hour.</div>;
  }
  if (state.kind === 'out-of-scope') {
    return <div className="pb-line pb-state">{state.note || `Not computed for ${span}.`}</div>;
  }
  if (state.kind === 'not-computed') {
    return <div className="pb-line pb-state">Charged at {span}, but this scenario was not computed for this hour.</div>;
  }
  const at = state.at;
  const otherAt = other ? other.hours[String(hour)] : null;
  const otherName = other ? `${other.short.toLowerCase()} pricing` : null;
  return (
    <div className="macro-run-result pb-result">
      <div className="pb-hour-head">{span} · against {otherName || 'no charge'}</div>
      {otherAt ? <VsScenario at={at} other={otherAt} /> : <VsBaseline at={at} />}
      <ResultStatus result={result} />
      <DeltaLegend from={other ? other.short.toLowerCase() : 'no charge'} to={s.short.toLowerCase()} />
    </div>
  );
}

/** Congestion Pricing panel body: the scenarios computed ahead on one charging
 *  zone, at `hour`. `choice` is { id, against }: 'baseline' as the id is no
 *  charge; `against` is the scenario a choice is compared with. `day` is
 *  /prebaked/day, { loading } or { error }, or null for a run of one hour. A
 *  scenario with no result at `hour` shows only why, no figures of other hours.
 *  A scenario with service levels is listed once and chosen at `serviceLevel`, which
 *  its toggle and table change for every page; `levelRanks` is LevelTable's `ranks`. */
export function PrebakedCpSection({ cp, hour, region, choice, result, onChoose, day = null,
  bands = { on: false, state: null }, onBands = () => {},
  serviceLevel = DEFAULT_SERVICE_LEVEL, onServiceLevel = () => {}, levelRanks = null }) {
  const byId = Object.fromEntries(cp.scenarios.map((s) => [s.id, s]));
  const noCharge = choice?.id === 'baseline';
  const chosen = choice && !noCharge ? byId[choice.id] || null : null;
  const chosenBase = chosen ? byId[chosen.variant_of] || chosen : null;
  const levels = chosenBase ? packLevels(cp.scenarios, chosenBase.id) : null;
  const pickLevel = (level) => {
    onServiceLevel(level);
    onChoose(idAtLevel(cp.scenarios, chosenBase.id, level), choice.against);
  };
  const against = chosen ? choice.against || chosen.compared_against : null;
  const other = against && against !== 'baseline' ? byId[against] || null : null;
  // Which scenario to compare with only matters for an hour that has a result.
  const comparable = chosen && chosen.compared_against !== 'baseline' && byId[chosen.compared_against]
    && scenarioHour(chosen, hour, cp.toll_windows_h).kind === 'computed';
  const windows = windowsText(cp.toll_windows_h, cp.window_labels);
  const hourState = chosen ? scenarioHour(chosen, hour, cp.toll_windows_h) : null;
  const hourAt = hourState?.kind === 'computed' ? hourState.at : null;
  const dayOf = chosen && day?.scenarios ? day.scenarios.find((x) => x.id === chosen.id) || null : null;
  const minutes = Math.round((cp.toll_usd / cp.vot_usd_per_hour) * 60);
  return (
    <div className="scenario-controls">
      <div className="scenario-controls-head">{cp.cordon.name} · computed ahead</div>
      <div className="pb-line">{usd2(cp.toll_usd)} per entry into the zone, {windows}.</div>
      <div className="pb-scenarios" role="group" aria-label="Pricing scenario">
        <ScenarioButton n={1} label="No charge" active={noCharge} onClick={() => onChoose('baseline')} />
        {cp.scenarios.filter((s) => !s.variant_of).map((s) => (
          <ScenarioButton key={s.id} n={s.n} label={s.short} active={chosenBase?.id === s.id}
            onClick={() => onChoose(idAtLevel(cp.scenarios, s.id, serviceLevel), s.compared_against)} />
        ))}
      </div>
      {levels && (
        <ServiceLevelToggle className="pb-level-toggle" levels={levels.map((l) => l.level)}
          value={levelOf(chosen)} onChange={pickLevel} />
      )}
      {comparable && (
        <div className="pb-against" role="group" aria-label="Compare with">
          <span className="pb-against-label">Compare with</span>
          <button type="button" className={`pb-against-btn ${other ? 'active' : ''}`}
            aria-pressed={!!other} onClick={() => onChoose(chosen.id, chosen.compared_against)}>
            {byId[chosen.compared_against].short}
          </button>
          <button type="button" className={`pb-against-btn ${!other ? 'active' : ''}`}
            aria-pressed={!other} onClick={() => onChoose(chosen.id, 'baseline')}>
            No charge
          </button>
        </div>
      )}
      {noCharge && <BaselineHour cp={cp} hour={hour} day={day} />}
      {chosen && <ScenarioResult cp={cp} s={chosen} other={other} hour={hour} result={result} />}
      {hourAt && chosen.route_cost && hourAt.route_cost && (
        <CorridorSection s={chosen} other={byId[chosen.compared_against] || null} hourAt={hourAt} hour={hour}
          bands={bands} onBands={onBands} labels={cp.window_labels} />
      )}
      {hourAt?.transit && (
        <TransitSection s={chosen} hourAt={hourAt} hour={hour} other={byId[chosen.compared_against] || null}
          labels={cp.window_labels} />
      )}
      {levels && (
        <LevelTable levels={levels} value={levelOf(chosen)} ranks={levelRanks}
          funder={byId[chosenBase.compared_against] || null} onPick={pickLevel} />
      )}
      {hourAt && chosen.class_order && (
        <ClassSection labels={cp.class_labels} order={chosen.class_order}
          hourRows={hourAt?.classes || null}
          hourOther={(other && hourAt && other.hours[String(hour)]?.classes) || null}
          dayRows={dayOf?.totals?.scenario.classes || null}
          dayOther={(other && dayOf?.totals?.against?.classes) || null}
          otherName={other ? `${other.short.toLowerCase()} pricing` : null}
          hourWhen={hourSpan(hour)}
          dayWhen={dayOf ? (dayOf.wholeDay ? 'All day' : hoursText(dayOf.scope, cp.window_labels)) : null} />
      )}
      {(noCharge || hourAt) && <DayTotals day={day} chosen={chosen} other={other} labels={cp.window_labels} />}
      {hourAt && (
        <div className="macro-run-note">
          {chosen.class_order
            ? `Each driver weighs an entry by the toll their income class pays, at ${usd2(cp.vot_usd_per_hour)}/h of travel time: `
            : `Drivers weigh each entry like ${minutes} min of travel time (${usd2(cp.toll_usd)} at ${usd2(cp.vot_usd_per_hour)}/h): `}
          some switch to transit, the others pay or route around the zone.
          {chosen.method_note && ` ${chosen.method_note}`}
          {' '}{region} is too large to simulate on request, so these scenarios were computed ahead
          {isLiveSimulation() ? '; drawing your own zone works on smaller metros.' : '.'}
        </div>
      )}
    </div>
  );
}

function fmtHour(h) {
  const n = Math.round(h);
  return n === 12 ? '12 pm' : n > 12 ? `${n - 12} pm` : `${n} am`;
}

/** School Zones panel body: the citywide school-zone rule computed ahead. */
export function PrebakedSzSection({ edit, region, active, result, onShow }) {
  const p = edit.policy;
  const s = edit.stats;
  return (
    <div className="scenario-controls">
      <div className="scenario-controls-head">{region} · computed ahead</div>
      <div className="pb-line">
        {p.speed_mph} mph within {Math.round(p.radius_m)} m of {p.schools} schools:
        {' '}{int(p.edges_changed)} road segments, {int(p.affected_km)} km.
      </div>
      {!active ? (
        <button className="macro-run-btn" onClick={onShow}>Show the result</button>
      ) : (
        <div className="macro-run-result pb-result">
          <div className="macro-run-grid">
            <div><span>{pct(s.passages[0], s.passages[1])}</span>traffic on the capped roads</div>
            <div><span>{int(s.edges_moved)}</span>segments change by 100+ vehicles and 10%</div>
            <div><span>{pct(s.total_vmt_mi[0], s.total_vmt_mi[1], 2)}</span>vehicle miles, whole region</div>
            <div><span>{int(s.congested_edges[0])} → {int(s.congested_edges[1])}</span>congested segments</div>
          </div>
          <ResultStatus result={result} />
          <DeltaLegend />
          <div className="macro-run-note">
            Freeways and ramps keep their limits. {region} is too large to simulate on request,
            {' '}so this rule was computed ahead.
          </div>
        </div>
      )}
    </div>
  );
}

/** One line on what an edit changes. */
function describe(edit) {
  if (edit.kind === 'signal') {
    const g = edit.signal;
    return `Green ${g.greens_before.map(Math.round).join('/')} → `
      + `${g.greens_after.map(Math.round).join('/')} s, ${Math.round(g.cycle_s)} s cycle`;
  }
  if (edit.removed.length) return `Closed: ${edit.removed.length} segments`;
  const drops = Object.values(edit.changes).filter((c) => c.lanes && c.lanes[1] < c.lanes[0]).length;
  return `${drops} segments lose a lane`;
}

function EditResult({ edit, result, onClear }) {
  const s = edit.stats;
  return (
    <div className="macro-run-result pb-result">
      <div className="macro-run-title">{edit.label}</div>
      <div className="macro-run-grid">
        <div><span>{pct(s.passages[0], s.passages[1])}</span>
          {edit.kind === 'signal' ? 'traffic on the approaches' : 'traffic on the edited road'}</div>
        <div><span>{s.max_vc[0].toFixed(2)} → {s.max_vc[1].toFixed(2)}</span>
          {edit.kind === 'signal' ? 'worst approach V/C' : 'worst V/C on the road'}</div>
        <div><span>{int(s.edges_moved)}</span>segments change by 100+ vehicles and 10%</div>
        <div><span>{int(s.congested_edges[0])} → {int(s.congested_edges[1])}</span>congested segments</div>
      </div>
      {s.dropped_vehicles > 0 && (
        <div className="pb-line pb-warn">
          {int(s.dropped_vehicles)} trips could not avoid the closed road and are left out.
        </div>
      )}
      <ResultStatus result={result} />
      <DeltaLegend />
      <button className="case-study-report pb-back" onClick={onClear}>
        <FaUndo size={10} /> Back to baseline
      </button>
    </div>
  );
}

/** The Network Editor for a metro offered only as edits computed ahead. */
export function PrebakedEditsPanel({
  panelRef, position, isDragging, onDragStart, onClose,
  region, loading, edits, choiceId, result, onChoose, onClear,
}) {
  const roads = edits.filter((e) => e.kind === 'road');
  const signals = edits.filter((e) => e.kind === 'signal');
  const chosen = edits.find((e) => e.id === choiceId) || null;
  const row = (e) => (
    <button key={e.id} className={`pb-edit ${e.id === choiceId ? 'active' : ''}`}
      onClick={() => onChoose(e.id)}>
      <span className="pb-edit-label">{e.label.replace(/^Signal retiming: /, '')}</span>
      <span className="pb-edit-desc">{describe(e)}</span>
    </button>
  );
  return (
    <>
      <div className="ne-exit-float">
        <button className="ne-exit-btn" onClick={onClose} title="Leave the Network Editor">
          <FaSignOutAlt size={14} /> Exit Network Editor
        </button>
      </div>
      <div
        ref={panelRef}
        className={`ne-panel ${isDragging ? 'dragging' : ''}`}
        style={{ left: position.x, top: position.y, cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={onDragStart}
      >
        <div className="ne-panel-header">
          <span>Network Editor{region ? ` — ${region}` : ''}</span>
          <button className="ne-close-btn" onClick={onClose} title="Close"><FaTimes size={14} /></button>
        </div>
        {loading ? (
          <div className="ne-loading">Loading {region || 'the'} edits…</div>
        ) : (
          <div className="ne-body pb-body">
            <div className="pb-intro">
              Edits computed ahead: this network is too large to simulate on request.
              Pick one here or click a highlighted road or signal on the map.
            </div>
            {roads.length > 0 && <div className="ne-section-label">Roads</div>}
            {roads.map(row)}
            {signals.length > 0 && <div className="ne-section-label">Signals</div>}
            {signals.map(row)}
            {chosen && <EditResult edit={chosen} result={result} onClear={onClear} />}
          </div>
        )}
      </div>
    </>
  );
}
